import React, { useState } from 'react'
import { useTheme } from '@/context/ThemeContext'
import type { WorkspaceRole } from '../types'
import ProspectReviewSection from './ProspectReviewSection'
import ProspectingUsageSection from './ProspectingUsageSection'

export type ProspectsView = 'review' | 'usage'

type Props = {
  workspaceId: string
  role: WorkspaceRole
  initialView?: ProspectsView
}

const VIEWS: { value: ProspectsView; label: string }[] = [
  { value: 'review', label: 'Review' },
  { value: 'usage', label: 'Team usage' },
]

/**
 * Sales → Prospects. Team usage is Owner/Admin only (ZAI-393): members never
 * see the switch (and the API refuses them as well), so for them this is just
 * the review queue.
 */
const ProspectsSection: React.FC<Props> = ({ workspaceId, role, initialView = 'review' }) => {
  const { currentTheme: t } = useTheme()
  const [view, setView] = useState<ProspectsView>(initialView)
  const canSeeUsage = role === 'owner' || role === 'admin'
  const activeView: ProspectsView = canSeeUsage ? view : 'review'

  return (
    <div className="flex flex-col gap-5">
      {canSeeUsage && (
        <div role="tablist" aria-label="Prospects view" className="flex items-center gap-1 self-start rounded-lg p-1" style={{ backgroundColor: t.hoverBackground }}>
          {VIEWS.map(({ value, label }) => {
            const selected = activeView === value
            return (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setView(value)}
                className="h-8 px-3 rounded-md text-[13px] font-medium transition-colors"
                style={selected
                  ? { backgroundColor: t.secondary, color: t.text, boxShadow: t.cardShadow }
                  : { color: t.secondrytext }}
              >
                {label}
              </button>
            )
          })}
        </div>
      )}
      {activeView === 'review'
        ? <ProspectReviewSection workspaceId={workspaceId} />
        : <ProspectingUsageSection workspaceId={workspaceId} />}
    </div>
  )
}

export default ProspectsSection
